import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Scissors, Search, MoreHorizontal, Edit3, Trash2,
  Archive, Eye, Download, Clock, Loader2, AlertCircle,
} from 'lucide-react';
import Sidebar, { SidebarProvider } from '../../../components/navigation/Sidebar';
import { patternService } from './services/patternService';

const DIFFICULTY_COLORS = {
  beginner:     { bg: '#D1FAE5', text: '#065F46' },
  easy:         { bg: '#DBEAFE', text: '#1E40AF' },
  intermediate: { bg: '#FEF3C7', text: '#92400E' },
  advanced:     { bg: '#FCE7F3', text: '#9D174D' },
};

const STATUS_BADGE = {
  draft:     { label: 'Draft',     bg: '#F3F4F6', text: '#6B7280' },
  published: { label: 'Published', bg: '#D1FAE5', text: '#065F46' },
  archived:  { label: 'Archived',  bg: '#F3F4F6', text: '#9CA3AF' },
};

// ─── Empty state ──────────────────────────────────────────────────────────────
function EmptyState({ onNew }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
           style={{ background: 'linear-gradient(135deg, #FAF7F4 0%, #F0EAE0 100%)', border: '2px dashed #C4B8A8' }}>
        <Scissors size={32} style={{ color: '#6B5744' }} />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        Your Pattern Studio is empty
      </h3>
      <p className="text-gray-500 text-sm max-w-xs mb-8">
        Create your first professional crochet pattern PDF in minutes. Export Etsy-ready files that buyers love.
      </p>
      <button
        onClick={onNew}
        className="flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95"
        style={{ background: 'linear-gradient(135deg, #6B5744 0%, #8B7355 100%)' }}
      >
        <Plus size={16} />
        Create My First Pattern
      </button>
    </div>
  );
}

// ─── Pattern card ──────────────────────────────────────────────────────────────
function PatternCard({ pattern, onEdit, onDelete, onDuplicate }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const d = pattern.pattern_data || {};
  const diffBadge = DIFFICULTY_COLORS[d.difficulty] || DIFFICULTY_COLORS.beginner;
  const statusBadge = STATUS_BADGE[pattern.status] || STATUS_BADGE.draft;
  const coverPhoto = d.photos?.find(p => p.category === 'cover');

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all group"
    >
      {/* Cover image */}
      <div
        className="relative w-full h-44 cursor-pointer"
        style={{ background: 'linear-gradient(135deg, #FAF7F4 0%, #F0EAE0 100%)' }}
        onClick={() => onEdit(pattern.id)}
      >
        {coverPhoto ? (
          <img src={coverPhoto.url} alt={pattern.title}
               className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Scissors size={40} style={{ color: '#C4B8A8' }} />
          </div>
        )}
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="flex gap-2">
            <button
              onClick={e => { e.stopPropagation(); onEdit(pattern.id); }}
              className="p-2 bg-white rounded-lg shadow text-gray-700 hover:bg-gray-50"
            >
              <Edit3 size={14} />
            </button>
            <button
              onClick={e => { e.stopPropagation(); navigate(`/etsy/pattern-studio/${pattern.id}/preview`); }}
              className="p-2 bg-white rounded-lg shadow text-gray-700 hover:bg-gray-50"
            >
              <Eye size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Card body */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-gray-900 truncate text-sm">
              {pattern.title || 'Untitled Pattern'}
            </h3>
            {d.animal_type && (
              <p className="text-xs text-gray-400 mt-0.5">{d.animal_type}</p>
            )}
          </div>
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-1 rounded-lg hover:bg-gray-100 text-gray-400"
            >
              <MoreHorizontal size={16} />
            </button>
            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute right-0 top-8 bg-white rounded-xl shadow-xl border border-gray-100 z-20 w-40 py-1"
                  onMouseLeave={() => setMenuOpen(false)}
                >
                  <button onClick={() => { onEdit(pattern.id); setMenuOpen(false); }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    <Edit3 size={13} /> Edit
                  </button>
                  <button onClick={() => { navigate(`/etsy/pattern-studio/${pattern.id}/preview`); setMenuOpen(false); }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    <Eye size={13} /> Preview
                  </button>
                  <button onClick={() => { onDuplicate(pattern.id); setMenuOpen(false); }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    <Download size={13} /> Duplicate
                  </button>
                  <div className="my-1 border-t border-gray-100" />
                  <button onClick={() => { onDelete(pattern.id); setMenuOpen(false); }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50">
                    <Trash2 size={13} /> Delete
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Badges row */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <span className="px-2 py-0.5 rounded-full text-xs font-medium"
                style={{ background: statusBadge.bg, color: statusBadge.text }}>
            {statusBadge.label}
          </span>
          {d.difficulty && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium capitalize"
                  style={{ background: diffBadge.bg, color: diffBadge.text }}>
              {d.difficulty}
            </span>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-1 mt-3 text-xs text-gray-400">
          <Clock size={11} />
          <span>{new Date(pattern.updated_at).toLocaleDateString()}</span>
          {d.sections?.length > 0 && (
            <span className="ml-auto">{d.sections.length} section{d.sections.length !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Main dashboard ───────────────────────────────────────────────────────────
export default function PatternStudioDashboard() {
  const navigate = useNavigate();
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await patternService.getAll();
      setPatterns(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id) {
    if (!confirm('Delete this pattern? This cannot be undone.')) return;
    await patternService.delete(id);
    setPatterns(p => p.filter(x => x.id !== id));
  }

  async function handleDuplicate(id) {
    const orig = patterns.find(p => p.id === id);
    if (!orig) return;
    try {
      const created = await patternService.create({
        title: `${orig.title} (copy)`,
        pattern_data: orig.pattern_data,
      });
      setPatterns(prev => [created, ...prev]);
    } catch (e) {
      alert('Could not duplicate: ' + e.message);
    }
  }

  const displayed = patterns.filter(p => {
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || p.status === filter;
    return matchSearch && matchFilter;
  });

  const TABS = ['all', 'draft', 'published', 'archived'];

  return (
    <SidebarProvider>
      <div className="flex min-h-screen" style={{ background: '#FAF7F4', fontFamily: "'Inter','Poppins',sans-serif" }}>
        <Sidebar />
        <main className="flex-1 min-w-0 p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Scissors size={22} style={{ color: '#6B5744' }} />
                Pattern Studio
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">Create beautiful crochet pattern PDFs for Etsy</p>
            </div>
            <button
              onClick={() => navigate('/etsy/pattern-studio/new')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all active:scale-95"
              style={{ background: 'linear-gradient(135deg, #6B5744 0%, #8B7355 100%)' }}
            >
              <Plus size={16} />
              New Pattern
            </button>
          </div>

          {/* Toolbar */}
          {patterns.length > 0 && (
            <div className="flex items-center gap-4 mb-6 flex-wrap">
              <div className="flex gap-1 p-1 bg-white rounded-xl border border-gray-200">
                {TABS.map(t => (
                  <button key={t}
                    onClick={() => setFilter(t)}
                    className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-all ${
                      filter === t ? 'bg-gray-900 text-white font-medium' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 flex-1 min-w-48 max-w-64">
                <Search size={14} className="text-gray-400 flex-shrink-0" />
                <input
                  value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search patterns…"
                  className="text-sm text-gray-700 outline-none flex-1 bg-transparent"
                />
              </div>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 size={28} className="animate-spin" style={{ color: '#6B5744' }} />
            </div>
          ) : error ? (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm max-w-md">
              <AlertCircle size={16} />
              <span>{error}</span>
              <button onClick={load} className="ml-auto underline text-xs">Retry</button>
            </div>
          ) : patterns.length === 0 ? (
            <EmptyState onNew={() => navigate('/etsy/pattern-studio/new')} />
          ) : displayed.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-gray-400">
              <Search size={32} className="mb-4 opacity-40" />
              <p className="text-sm">No patterns match your search.</p>
            </div>
          ) : (
            <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              <AnimatePresence>
                {displayed.map(p => (
                  <PatternCard
                    key={p.id}
                    pattern={p}
                    onEdit={id => navigate(`/etsy/pattern-studio/${id}/edit`)}
                    onDelete={handleDelete}
                    onDuplicate={handleDuplicate}
                  />
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </main>
      </div>
    </SidebarProvider>
  );
}
